/* Message (rules 3, 14, 26, 46; spec 6.2; thread structure after assistant-ui, MIT).
   Assistant replies show a hover/focus action strip (Copy, Good, Bad, "⋯"); on touch only "⋯",
   which opens as a sheet; long-press (touch) or right-click (desktop) opens the same menu. */
import { useRef, useState, type ReactNode } from 'react';
import {
  BadgeCheck, Bookmark, ChevronLeft, ChevronRight, Coins, Columns3, Copy, Download, Flag, GitBranch, LoaderCircle,
  Pencil, RefreshCw, Share2, ThumbsDown, ThumbsUp, Volume2,
} from 'lucide-react';
import type { Citation } from '../lib/api-types';
import { Menu, type MenuGroupSpec } from './Menu';
import { AILabel, Button, CapBadge } from './basics';
import { MarkdownStream } from './MarkdownStream';
import { MemoryChip, type MemoryItem } from './trust';
import { OptionalSlot, optionalComponent } from './optional';
import { cx } from './cx';

/* Feature components the message menu and footer use when installed (lazy, each with a fallback). */
type ReportDialogProps = { open: boolean; onClose: () => void; targetType: string; targetId: string; excerpt?: string };
type ShareDialogProps = { open: boolean; onClose: () => void; resourceType: string; resourceId: string; title?: string; snapshot?: unknown };
type MemoryUsedChipProps = { items: MemoryItem[]; onDisableForChat?: (id: string) => void };
const ReportDialog = optionalComponent<ReportDialogProps>(import.meta.glob('../features/settings/ReportDialog.tsx'), 'ReportDialog');
const ShareDialog = optionalComponent<ShareDialogProps>(import.meta.glob('../features/collab/ShareDialog.tsx'), 'ShareDialog');
const MemoryUsedChip = optionalComponent<MemoryUsedChipProps>(import.meta.glob('../features/memory/MemoryUsedChip.tsx'), 'MemoryUsedChip');

/** What "Share" in a message or artifact menu shares (collab ShareDialog resource types). */
export interface ShareTarget { resourceType: 'chat' | 'doc' | 'sheet' | 'deck' | 'artifact' | 'project' | 'task_replay'; resourceId: string; title?: string; snapshot?: unknown }

export interface BranchInfo { index: number; count: number; onPrev: () => void; onNext: () => void }

export function BranchPicker({ index, count, onPrev, onNext }: BranchInfo) {
  if (count < 2) return null;
  return (
    <span className="branch" role="group" aria-label="Reply versions">
      <button type="button" className="icon-btn sm" aria-label="Previous version" disabled={index === 0} onClick={onPrev}><ChevronLeft size={14} aria-hidden="true" /></button>
      <span aria-live="polite">{index + 1} / {count}</span>
      <button type="button" className="icon-btn sm" aria-label="Next version" disabled={index >= count - 1} onClick={onNext}><ChevronRight size={14} aria-hidden="true" /></button>
    </span>
  );
}

export interface MessageActions {
  onCopy?: () => void;
  onFeedback?: (v: 'good' | 'bad' | null) => void;
  onRegenerate?: () => void;
  onBranch?: () => void;
  onCompare?: () => void;
  onTryModel?: (model: string) => void;
  onReadAloud?: () => void;
  onCheckSources?: () => void;
  onSaveMemory?: () => void;
  onShare?: () => void;
  onExport?: () => void;
  onReport?: () => void;
}

export interface MessageProps {
  role: 'user' | 'assistant';
  /** Markdown text (assistant) or plain text (user). */
  text?: string;
  /** Custom content instead of / after the text (inline widgets, tables). */
  children?: ReactNode;
  streaming?: boolean;
  /** Named status before the first token (rule 15): "Searching 6 sites". */
  status?: ReactNode;
  steps?: ReactNode;
  /** After the content: source strip, artifacts, quizzes. */
  extras?: ReactNode;
  /** Model attribution line (rule 26), memory chip. */
  footer?: ReactNode;
  branch?: BranchInfo;
  stopped?: boolean;
  onContinue?: () => void;
  error?: ReactNode;
  latest?: boolean;
  credits?: number;
  actions?: MessageActions;
  onEdit?: () => void;
  name?: string;
  models?: string[];
  className?: string;
  /** Turns `[n]` markers in `text` into citation chips. */
  citations?: Citation[];
  onCite?: (c: Citation) => void;
  /** Message id: the report target when no `actions.onReport` is given. */
  id?: string;
  /** Offers the collab ShareDialog from "Share" when no `actions.onShare` is given. */
  share?: ShareTarget;
  /** Memories this reply used (MEM-01): rendered with the memory feature's chip (Edit opens the item). */
  memory?: MemoryItem[];
  onDisableMemoryForChat?: (id: string) => void;
  /** Extra menu groups (feature-specific items such as "Delete this turn"), placed before "This reply". */
  menuGroups?: MenuGroupSpec[];
  /** Label of the button on a stopped reply (default "Continue"). */
  continueLabel?: string;
}

const LONG_PRESS_MS = 500;

export function Message(p: MessageProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [fb, setFb] = useState<'good' | 'bad' | null>(null);
  const [dialog, setDialog] = useState<'report' | 'share' | null>(null);
  const press = useRef<ReturnType<typeof setTimeout> | null>(null);
  const a = p.actions ?? {};
  const copy = () => {
    if (a.onCopy) a.onCopy();
    else if (p.text) navigator.clipboard?.writeText(p.text).catch(() => undefined);
  };
  const feedback = (v: 'good' | 'bad') => { const next = fb === v ? null : v; setFb(next); a.onFeedback?.(next); };

  if (p.role === 'user') {
    return (
      <article className={cx('msg user', p.className)} aria-label="You said">
        <div className="msg-b">
          {p.children}
          {p.text && <div className="bubble">{p.text}</div>}
          <div className="msg-actions">
            {p.onEdit && <button type="button" className="icon-btn sm" aria-label="Edit and branch" onClick={p.onEdit}><Pencil size={14} aria-hidden="true" /></button>}
            <button type="button" className="icon-btn sm" aria-label="Copy" onClick={copy}><Copy size={14} aria-hidden="true" /></button>
          </div>
        </div>
      </article>
    );
  }

  const models = p.models ?? ['Fast', 'Balanced', 'Frontier', 'Heavy', 'On-device'];
  const groups: MenuGroupSpec[] = [
    {
      id: 'quick', label: 'Quick', sheetOnly: true, items: [
        { id: 'copy', label: 'Copy', icon: Copy, onSelect: copy },
        { id: 'good', label: 'Good reply', icon: ThumbsUp, onSelect: () => feedback('good') },
        { id: 'bad', label: 'Bad reply', icon: ThumbsDown, onSelect: () => feedback('bad') },
      ],
    },
    {
      id: 'reply', label: 'Reply', items: [
        { id: 'regen', label: 'Regenerate', icon: RefreshCw, onSelect: a.onRegenerate },
        { id: 'branch', label: 'Branch from here', icon: GitBranch, onSelect: a.onBranch },
        { id: 'compare', label: 'Compare side by side', icon: Columns3, cap: 'MODEL-05', onSelect: a.onCompare },
      ],
    },
    { id: 'models', label: 'Try another model', chips: true, items: models.map((m) => ({ id: `m-${m}`, label: m, onSelect: () => a.onTryModel?.(m) })) },
    {
      id: 'listen', label: 'Listen and check', items: [
        { id: 'read', label: 'Read aloud', icon: Volume2, cap: 'VOICE-05', onSelect: a.onReadAloud },
        { id: 'sources', label: 'Check sources', icon: BadgeCheck, onSelect: a.onCheckSources },
      ],
    },
    {
      id: 'keep', label: 'Keep and share', items: [
        { id: 'mem', label: 'Save to memory', icon: Bookmark, onSelect: a.onSaveMemory },
        { id: 'share', label: 'Share', icon: Share2, cap: p.share && !a.onShare ? 'COLLAB-01' : undefined, onSelect: a.onShare ?? (p.share && ShareDialog ? () => setDialog('share') : undefined) },
        { id: 'export', label: 'Export: DOCX, PDF, Google Doc', icon: Download, onSelect: a.onExport },
      ],
    },
    {
      id: 'this', label: 'This reply', items: [
        ...(p.credits !== undefined ? [{ id: 'credits', label: `Used ${p.credits} credits`, icon: Coins, disabled: true }] : []),
        { id: 'report', label: 'Report this reply', icon: Flag, danger: true, cap: 'SAFE-03', onSelect: a.onReport ?? (ReportDialog ? () => setDialog('report') : undefined) },
      ],
    },
  ];
  if (p.menuGroups?.length) groups.splice(groups.length - 1, 0, ...p.menuGroups);

  const openMenu = (asSheet: boolean) => { setSheet(asSheet); setMenuOpen(true); };
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    press.current = setTimeout(() => openMenu(true), LONG_PRESS_MS);
  };
  const cancelPress = () => { if (press.current) clearTimeout(press.current); press.current = null; };

  return (
    <article
      className={cx('msg assistant', p.latest && 'latest', p.className)}
      aria-label={`${p.name ?? 'Crosspoint AI +'} reply`}
      aria-busy={p.streaming || undefined}
      data-cap="CHAT-01"
      onPointerDown={onPointerDown}
      onPointerUp={cancelPress}
      onPointerLeave={cancelPress}
      onPointerCancel={cancelPress}
      onContextMenu={(e) => { if ((e.target as HTMLElement).closest('a,button,input,textarea')) return; e.preventDefault(); openMenu(false); }}
    >
      <div className="msg-av" aria-hidden="true">+</div>
      <div className="msg-b">
        <div className="msg-meta">
          <b>{p.name ?? 'Crosspoint AI +'}</b>
          <AILabel />
          {p.branch && <BranchPicker {...p.branch} />}
        </div>
        {p.steps}
        {p.status && !p.text && (
          <div className="msg-status" role="status">
            <LoaderCircle size={16} className="spin" aria-hidden="true" />
            <span>{p.status}</span>
          </div>
        )}
        <div className="msg-content">
          {p.text ? <MarkdownStream streaming={p.streaming} citations={p.citations} onCite={p.onCite}>{p.text}</MarkdownStream> : null}
          {p.children}
        </div>
        {p.stopped && (
          <div className="msg-stopped">
            <span className="chip">Stopped</span>
            {p.onContinue && <Button size="sm" onClick={p.onContinue}>{p.continueLabel ?? 'Continue'}</Button>}
          </div>
        )}
        {p.error}
        {p.extras}
        {(p.footer || p.memory?.length) && (
          <div className="msg-foot">
            {p.footer}
            {p.memory?.length ? (
              <OptionalSlot
                C={MemoryUsedChip}
                props={{ items: p.memory, onDisableForChat: p.onDisableMemoryForChat }}
                fallback={<MemoryChip items={p.memory} onDisableForChat={p.onDisableMemoryForChat} />}
              />
            ) : null}
          </div>
        )}
        {dialog === 'report' && (
          <OptionalSlot C={ReportDialog} props={{ open: true, onClose: () => setDialog(null), targetType: 'message', targetId: p.id ?? 'unsaved', excerpt: p.text?.slice(0, 2000) }} />
        )}
        {dialog === 'share' && p.share && (
          <OptionalSlot C={ShareDialog} props={{ open: true, onClose: () => setDialog(null), ...p.share }} />
        )}
        {!p.streaming && (
          <div className="msg-actions" role="toolbar" aria-label="Message actions" data-open={menuOpen || undefined} data-cap="CHAT-03">
            <CapBadge id="CHAT-03" />
            <button type="button" className="icon-btn strip-i" aria-label="Copy" onClick={copy}><Copy size={16} aria-hidden="true" /></button>
            <button type="button" className="icon-btn strip-i" aria-label="Good reply" aria-pressed={fb === 'good'} onClick={() => feedback('good')}><ThumbsUp size={16} aria-hidden="true" /></button>
            <button type="button" className="icon-btn strip-i" aria-label="Bad reply" aria-pressed={fb === 'bad'} onClick={() => feedback('bad')}><ThumbsDown size={16} aria-hidden="true" /></button>
            <Menu
              label="More actions for this reply"
              groups={groups}
              open={menuOpen}
              onOpenChange={(o) => { setMenuOpen(o); if (!o) setSheet(false); }}
              asSheet={sheet ? true : 'auto'}
              align="start"
            />
          </div>
        )}
      </div>
    </article>
  );
}
